import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useBulkUpdateFact } from '@/api/mutations/useBulkUpdateFact';
import { useSelectedFactsDb } from '@/hooks/useSelectedFactsDb';
import { RelevanceSelector } from '@/new/components/fact/relevance-selector';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Relevance } from '@/types';

export function UpdateRelevanceButton({ selectedFactIds }: { selectedFactIds: Set<string> }) {
  const { subsetId } = useParams();
  if (!subsetId) return null;

  const { getAllSelectedIds } = useSelectedFactsDb();

  const [selectedRelevance, setSelectedRelevance] = useState<Relevance>(Relevance.Relevant);
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: updateFacts, isLoading } = useBulkUpdateFact(subsetId);

  const handleConfirm = async () => {
    const factIds = await getAllSelectedIds(subsetId);
    trackCustomEvent(TrackingEventsV2.BulkUpdateFact, {
      subsetId,
      count: factIds.length,
    });
    updateFacts(
      { relevance: selectedRelevance, factIds },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button disabled={selectedFactIds.size === 0}>
          <Icon className="lucide--copy-check" />
          Update Relevance
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--copy-check" />
            Bulk Update Relevance
          </DialogTitle>
        </DialogHeader>

        <Label>New Relevance Level</Label>
        <RelevanceSelector relevanceText={selectedRelevance} updateRelevance={setSelectedRelevance} />

        <Button
          data-testid="confirm-bulk-update-button"
          variant="primary"
          onClick={handleConfirm}
          disabled={isLoading}
          className="mt-4"
        >
          {isLoading ? 'Updating...' : `Update (${selectedFactIds.size})`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
